import EditRubricForm from 'components/Rubrics/EditRubricForm';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormValidation from 'services/hooks/useFormValidation';
import rubricService from 'services/rubric';
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    Loading,
    MenuItem,
    Paper,
    ResponsiveDialog,
    Select,
    Stack,
    styled,
    Text,
    TextArea,
    TextInput,
    Tooltip,
} from 'shell/components';
import { RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import {
    getRubric,
    getTooltipComponent,
    getWordCount,
    isNatural,
    isValidRate,
    roundCriteriaOptionRates,
    sortCriteria,
} from 'utils/helper';
import { v4 as uuid } from 'uuid';

const defaultForm = {
    numCriteria: '2',
    useCustomCriteria: false,
    criteria: [
        {
            id: uuid(),
            title: '',
            description: '',
        },
        {
            id: uuid(),
            title: '',
            description: '',
        },
    ],
    maxScore: '',
    numOptions: '2',
};

const defaultRubricErrors = {
    title: '',
    criteria: {},
};

const CreateRubricAIDialog = memo(({ open, title, description, onClose, onCreate }) => {
    const { course, setRubrics } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const isCancelled = useRef(false);
    const [form, setForm] = useState(defaultForm);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [rubricErrors, setRubricErrors] = useState(defaultRubricErrors);
    const [hasClickedSubmitRubric, setHasClickedSubmitRubric] = useState(false);
    const [hasClickedSaveAsTemplate, setHasClickedSaveAsTemplate] = useState(false);

    const handleClose = () => {
        setDialog({
            open: true,
            message: t('dialog.exit-rubric-creation.title', 'Exit Rubric Creation?'),
            description: t(
                'dialog.exit-rubric-creation.description',
                'If you leave this page before the rubric is saved you will lose all of the details you entered during setup.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                onClose();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const getErrorMessage = useCallback(
        (key, value, form) => {
            switch (key) {
                case 'criteria':
                    if (!form.useCustomCriteria) return undefined;
                    return form.criteria.map((criterion) => ({
                        title: !criterion.title.trim()
                            ? t('rubric.create-ai-rubric.error.empty-criterion', 'Please enter a criterion name')
                            : '',
                    }));
                case 'maxScore':
                    if (!value.trim() || !isValidRate(value)) return t('error.empty-number', 'Please enter a number');
                    return '';
                default:
                    break;
            }
        },
        [t]
    );

    const { errors, validate, validateAll } = useFormValidation(form, getErrorMessage);

    const onChange = (key, value) => {
        if (key === 'useCustomCriteria' && value === false) {
            setDialog({
                open: true,
                message: t('dialog.criteria-delete-warning.title', 'Existing Criteria Will Be Deleted'),
                description: t(
                    'dialog.criteria-delete-warning.description',
                    'If you deselect the option to specify your own criteria, any criteria you have already created will be permanently deleted. Are you sure you want to continue?'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.continue', 'Continue'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        useCustomCriteria: false,
                        criteria: [
                            {
                                id: uuid(),
                                title: '',
                                description: '',
                            },
                            {
                                id: uuid(),
                                title: '',
                                description: '',
                            },
                        ],
                    }));
                    setHasClickedSaveAsTemplate(false);
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            setHasClickedSaveAsTemplate(false);
            setForm((current) => {
                const newForm = { ...current, [key]: value };

                if (key === 'numCriteria') {
                    newForm.criteria = Array.from(Array(Number(value))).map(() => ({
                        id: uuid(),
                        title: '',
                        description: '',
                    }));
                }

                if (hasClickedSubmit) {
                    validate(key, value, newForm);
                }

                return newForm;
            });
        }
    };

    const onClickAddCriterion = () => {
        setForm((current) => ({
            ...current,
            criteria: [
                ...current.criteria,
                {
                    id: uuid(),
                    title: '',
                    description: '',
                },
            ],
        }));
    };

    const onClickDeleteCriterion = (criterion) => {
        setDialog({
            open: true,
            message: t('dialog.delete-criterion.title', 'Delete Criterion?'),
            description: t(
                'dialog.delete-criterion.description',
                'Deleting this criterion is permanent and cannot be undone.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setForm((current) => ({
                    ...current,
                    criteria: current.criteria.filter(({ id }) => id !== criterion.id),
                }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickBack = () => {
        setDialog({
            open: true,
            message: t('dialog.return-to-settings.title', 'Returning to Settings Will Interrupt Rubric Generation'),
            description: t(
                'dialog.return-to-settings.description',
                'If you proceed, the rubric will not be generated. Do you want to continue?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setIsLoading(false);
                isCancelled.current = true;
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const validateTitle = useCallback(() => {
        const error = result.title.trim() ? '' : t('error.empty-name', 'Please enter a name');
        setRubricErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [result?.title, t]);

    const validateCriteria = useCallback(() => {
        let error = false;
        result.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isNatural(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setRubricErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? t('error.empty-number', 'Please enter a number') : '',
                                description: isDescriptionInvalid
                                    ? t('error.word-count-exceeded', 'Word count exceeded')
                                    : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [result?.criteria, t]);

    const onClickEditSettings = () => {
        setDialog({
            open: true,
            message: t('dialog.editing-settings-warning.title', 'Editing Settings Will Erase This Rubric'),
            description: t(
                'dialog.editing-settings-warning.description',
                'If you continue, you will need to generate a new rubric.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setResult(null);
                setRubricErrors({ title: '', criteria: {} });
                setHasClickedSubmitRubric(false);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const renderDialogContent = () => {
        if (isLoading) {
            return (
                <LoadingStack device={device}>
                    <Button
                        sx={{ alignSelf: 'flex-start' }}
                        startIcon={<Icon kind={'arrowLeft'} />}
                        variant={'borderless'}
                        onClick={onClickBack}
                    >
                        {t('common:controls.back', 'Back')}
                    </Button>
                    <CenterStack device={device} spacing={20}>
                        <Stack spacing={2}>
                            <Text align={'center'} component={'h1'} variant={'h6'} weight={'semibold'}>
                                {t('rubric.create-ai-rubric.loading.title', 'Your rubric is being generated!')}
                            </Text>
                            <Text align={'center'}>
                                {t(
                                    'keep-window-open',
                                    'Please keep this window open until this process completes. This may take a few minutes.'
                                )}
                            </Text>
                        </Stack>
                        <Loading />
                    </CenterStack>
                </LoadingStack>
            );
        }

        if (result) {
            return (
                <ResultStack alignItems={'flex-start'} spacing={5}>
                    <Stack spacing={2}>
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={4}>
                            <Text variant={'h6'} weight={'semibold'}>
                                {t('rubric.create-ai-rubric.review.title', 'Review Rubric')}
                            </Text>
                            {/* <Button startIcon={<Icon kind={'video'} />} variant={'borderless'}>
                                Best Practices for Rubric Creation
                            </Button> */}
                            {device === 'mobile' && (
                                <Button variant={'secondary'} onClick={onClickEditSettings}>
                                    {t('rubric.create-ai-rubric.review.edit-button', 'Edit Settings')}
                                </Button>
                            )}
                        </Stack>
                        <Text>
                            {t(
                                'rubric.create-ai-rubric.review.description',
                                'Please review and verify all AI-generated information for accuracy and completeness.'
                            )}
                        </Text>
                    </Stack>
                    {device !== 'mobile' && (
                        <CustomButton variant={'secondary'} onClick={onClickEditSettings}>
                            {t('rubric.create-ai-rubric.review.edit-button', 'Edit Settings')}
                        </CustomButton>
                    )}
                    <CustomEditRubricForm
                        device={device}
                        form={result}
                        setForm={setResult}
                        errors={rubricErrors}
                        setErrors={setRubricErrors}
                        hasClickedSubmit={hasClickedSubmitRubric}
                        validateTitle={validateTitle}
                        validateCriteria={validateCriteria}
                    />
                </ResultStack>
            );
        }

        return (
            <Stack spacing={5}>
                <Text>
                    {t(
                        'rubric.create-ai-rubric.form.description',
                        'Our AI leverages your assignment title and description to generate a rubric. You have the option to specify criteria manually or allow the AI to suggest criteria based on this information.'
                    )}
                </Text>
                <Stack
                    sx={{ width: '100%' }}
                    alignItems={'flex-end'}
                    direction={device === 'mobile' ? 'column' : 'row'}
                    justifyContent={'space-between'}
                >
                    <InputStack
                        alignItems={device === 'mobile' ? 'flex-start' : 'flex-end'}
                        device={device}
                        direction={device === 'mobile' ? 'column' : 'row'}
                        spacing={4}
                    >
                        <CustomSelect
                            device={device}
                            disabled={form.useCustomCriteria}
                            label={
                                <Stack alignItems={'center'} component={'span'} direction={'row'} spacing={2}>
                                    <Text>
                                        {t(
                                            'rubric.create-ai-rubric.form.number-of-criteria-label',
                                            'How many criteria will this rubric have?'
                                        )}
                                    </Text>
                                    <CustomTooltip
                                        content={getTooltipComponent(
                                            t(
                                                'rubric.create-ai-rubric.form.number-of-criteria-tooltip-title',
                                                'Creating criteria'
                                            ),
                                            t(
                                                'rubric.create-ai-rubric.form.number-of-criteria-tooltip-description',
                                                "Please indicate the number of criteria you wish to create. If you don't choose to specify your own criteria, they will be automatically generated based on the assignment description using AI. We recommend 5 or fewer for best results."
                                            )
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            labelId={'num-criteria-select'}
                            required
                            value={form.useCustomCriteria ? form.criteria.length.toString() : form.numCriteria}
                            onChange={(e) => onChange('numCriteria', e.target.value)}
                        >
                            <MenuItem value={'1'}>1</MenuItem>
                            <MenuItem value={'2'}>2</MenuItem>
                            <MenuItem value={'3'}>3</MenuItem>
                            <MenuItem value={'4'}>4</MenuItem>
                            <MenuItem value={'5'}>5</MenuItem>
                            <MenuItem value={'6'}>6</MenuItem>
                            <MenuItem value={'7'}>7</MenuItem>
                            <MenuItem value={'8'}>8</MenuItem>
                            <MenuItem value={'9'}>9</MenuItem>
                            <MenuItem value={'10'}>10</MenuItem>
                        </CustomSelect>
                        <Checkbox
                            checked={form.useCustomCriteria}
                            label={t('rubric.create-ai-rubric.form.specify-criteria-label', 'Specify my own criteria')}
                            onChange={(_, checked) => onChange('useCustomCriteria', checked)}
                        />
                    </InputStack>
                    {form.useCustomCriteria && (
                        <Button startIcon={<Icon kind={'add'} />} variant={'secondary'} onClick={onClickAddCriterion}>
                            {t('rubric.add-criterion', 'Add Criterion')}
                        </Button>
                    )}
                </Stack>
                {form.useCustomCriteria && (
                    <Stack sx={{ width: '100%' }} spacing={4}>
                        {form.criteria.map((criterion, index) => (
                            <CustomPaper key={criterion.id} variant={'outlined'}>
                                <Stack spacing={4}>
                                    <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                        <TextInput
                                            sx={{ width: '400px' }}
                                            aria-label={`criterion name for criterion ${index + 1}`}
                                            error={Boolean(errors.criteria?.[index]?.title)}
                                            helperText={errors.criteria?.[index]?.title}
                                            label={t(
                                                'rubric.create-ai-rubric.form.criterion-name-label',
                                                'Criterion Name'
                                            )}
                                            placeholder={t(
                                                'rubric.create-ai-rubric.form.criterion-name-placeholder',
                                                'ex. Organization'
                                            )}
                                            required
                                            value={form.criteria.find((c) => c.id === criterion.id).title}
                                            onChange={(e) => {
                                                onChange(
                                                    'criteria',
                                                    form.criteria.map((c) =>
                                                        c.id === criterion.id ? { ...c, title: e.target.value } : c
                                                    )
                                                );
                                            }}
                                        />
                                        {device !== 'mobile' && (
                                            <Button
                                                color={'neutral'}
                                                disabled={form.criteria.length <= 2}
                                                startIcon={<Icon kind={'delete'} />}
                                                variant={'tertiary'}
                                                onClick={() => onClickDeleteCriterion(criterion)}
                                            >
                                                {t('rubric.delete-criterion', 'Delete Criterion')}
                                            </Button>
                                        )}
                                    </Stack>
                                    {device === 'mobile' ? (
                                        <TextArea
                                            aria-label={`criterion description for criterion ${index + 1}`}
                                            helperText={t(
                                                'rubric.create-ai-rubric.form.criterion-description-helper-text',
                                                'If left blank, the AI will create a description for this criterion'
                                            )}
                                            label={t(
                                                'rubric.create-ai-rubric.form.criterion-description-label',
                                                'Criterion Description'
                                            )}
                                            placeholder={t(
                                                'rubric.create-ai-rubric.form.criterion-description-placeholder',
                                                'ex. Evaluates the clarity and coherence of the project structure, ensuring logical arrangement and easy comprehension.'
                                            )}
                                            rows={8}
                                            value={form.criteria.find((c) => c.id === criterion.id).description}
                                            onChange={(e) => {
                                                onChange(
                                                    'criteria',
                                                    form.criteria.map((c) =>
                                                        c.id === criterion.id
                                                            ? { ...c, description: e.target.value }
                                                            : c
                                                    )
                                                );
                                            }}
                                        />
                                    ) : (
                                        <TextInput
                                            aria-label={`criterion description for criterion ${index + 1}`}
                                            helperText={t(
                                                'rubric.create-ai-rubric.form.criterion-description-helper-text',
                                                'If left blank, the AI will create a description for this criterion'
                                            )}
                                            label={t(
                                                'rubric.create-ai-rubric.form.criterion-description-label',
                                                'Criterion Description'
                                            )}
                                            placeholder={t(
                                                'rubric.create-ai-rubric.form.criterion-description-placeholder',
                                                'ex. Evaluates the clarity and coherence of the project structure, ensuring logical arrangement and easy comprehension.'
                                            )}
                                            value={form.criteria.find((c) => c.id === criterion.id).description}
                                            onChange={(e) => {
                                                onChange(
                                                    'criteria',
                                                    form.criteria.map((c) =>
                                                        c.id === criterion.id
                                                            ? { ...c, description: e.target.value }
                                                            : c
                                                    )
                                                );
                                            }}
                                        />
                                    )}
                                    {device === 'mobile' && (
                                        <Button
                                            sx={{ alignSelf: 'flex-end' }}
                                            color={'neutral'}
                                            disabled={form.criteria.length <= 2}
                                            startIcon={<Icon kind={'delete'} />}
                                            variant={'tertiary'}
                                            onClick={() => onClickDeleteCriterion(criterion)}
                                        >
                                            {t('rubric.delete-criterion', 'Delete Criterion')}
                                        </Button>
                                    )}
                                </Stack>
                            </CustomPaper>
                        ))}
                    </Stack>
                )}
                <CustomTextInput
                    device={device}
                    error={Boolean(errors.maxScore)}
                    helperText={errors.maxScore}
                    label={t('rubric.create-ai-rubric.form.criterion-max-score-label', 'Max score for each criterion')}
                    placeholder={t('rubric.create-ai-rubric.form.criterion-max-score-placeholder', 'ex. 25')}
                    required
                    value={form.maxScore}
                    onChange={(e) => onChange('maxScore', e.target.value)}
                />
                <CustomSelect
                    device={device}
                    label={
                        <Stack alignItems={'center'} component={'span'} direction={'row'} spacing={2}>
                            <Text>
                                {t(
                                    'rubric.create-ai-rubric.form.criterion-levels-label',
                                    'How many rating levels will each criterion have?'
                                )}
                            </Text>
                            <CustomTooltip
                                content={getTooltipComponent(
                                    t(
                                        'rubric.create-ai-rubric.form.criterion-levels-tooltip-title',
                                        'What are rating levels?'
                                    ),
                                    t(
                                        'rubric.create-ai-rubric.form.criterion-levels-tooltip-description',
                                        'Rating levels in a rubric indicate the varying degrees of achievement or performance associated with each criterion. We recommend 8 or fewer for best results.'
                                    )
                                )}
                                type={'info'}
                            />
                        </Stack>
                    }
                    labelId={'num-options-select'}
                    required
                    value={form.numOptions}
                    onChange={(e) => onChange('numOptions', e.target.value)}
                >
                    <MenuItem value={'2'}>2</MenuItem>
                    <MenuItem value={'3'}>3</MenuItem>
                    <MenuItem value={'4'}>4</MenuItem>
                    <MenuItem value={'5'}>5</MenuItem>
                    <MenuItem value={'6'}>6</MenuItem>
                    <MenuItem value={'7'}>7</MenuItem>
                    <MenuItem value={'8'}>8</MenuItem>
                    <MenuItem value={'9'}>9</MenuItem>
                    <MenuItem value={'10'}>10</MenuItem>
                </CustomSelect>
            </Stack>
        );
    };

    const onClickGenerate = useCallback(async () => {
        isCancelled.current = false;
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            setIsLoading(true);
            const response = await getRubric({ ...form, title, description, language: course?.language });
            const data = await response.json();
            if (isCancelled.current) return;
            try {
                const { result } = data;
                setResult({
                    title: result.title,
                    criteria: result.criteria.map((criterion) => {
                        return {
                            ...criterion,
                            id: uuid(),
                            options: criterion.options
                                .sort((a, b) => {
                                    const numA = Number(a.rate);
                                    const numB = Number(b.rate);
                                    if (a.rate === '') return 1;
                                    if (b.rate === '') return -1;
                                    if (!numA && !numB) return 0;
                                    return numB - numA;
                                })
                                .map((option) => ({ ...option, id: uuid(), rate: option.rate.toString() })),
                        };
                    }),
                    sort: 'desc',
                });
            } catch (e) {
                message.error(
                    t(
                        'rubric.create-ai-rubric.error.rubric-generation-failed',
                        'Rubric generation failed, please try again.'
                    )
                );
            } finally {
                setIsLoading(false);
            }
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    }, [course?.language, description, form, message, t, title, validateAll]);

    const onClickSaveAsTemplate = useCallback(() => {
        setHasClickedSubmitRubric(true);
        const isValid = validateTitle() && validateCriteria();
        if (isValid) {
            setDialog({
                open: true,
                message: t(
                    'rubric.create-ai-rubric.save-rubric-warning.title',
                    'Did You Verify All AI-Generated Information?'
                ),
                description: t(
                    'rubric.create-ai-rubric.save-rubric-warning.description',
                    'Before proceeding, please confirm that you have reviewed and verified all AI-generated information for accuracy and completeness.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.confirm', 'Confirm'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    const timestamp = new Date().getTime();
                    const criteriaRoundedOptionRates = roundCriteriaOptionRates(result.criteria);
                    const sortedCriteria = sortCriteria(criteriaRoundedOptionRates, result.sort);
                    const rubric = {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        rubric: { ...result, criteria: sortedCriteria },
                        user_id: auth.user_id,
                    };
                    setHasClickedSaveAsTemplate(true);
                    setRubrics((current) => [...current, rubric]);
                    rubricService.createRubric(rubric);
                    message.success(t('message.rubric-saved-success', 'Rubric was saved successfully.'));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    }, [auth.user_id, closeDialog, message, result, setDialog, setRubrics, validateCriteria, validateTitle, t]);

    const onClickCreateRubric = useCallback(() => {
        setHasClickedSubmitRubric(true);
        const isValid = validateTitle() && validateCriteria();
        if (isValid) {
            setDialog({
                open: true,
                message: t('dialog.save-rubric-warning.title', 'Did You Verify All AI-Generated Information?'),
                description: t(
                    'dialog.save-rubric-warning.description',
                    'Before proceeding, please confirm that you have reviewed and verified all AI-generated information for accuracy and completeness.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.confirm', 'Confirm'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    const criteriaRoundedOptionRates = roundCriteriaOptionRates(result.criteria);
                    const sortedCriteria = sortCriteria(criteriaRoundedOptionRates, result.sort);
                    onCreate({ ...result, criteria: sortedCriteria });
                    onClose();
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    }, [closeDialog, message, onClose, onCreate, result, setDialog, validateCriteria, validateTitle, t]);

    const renderDialogActions = () => {
        if (isLoading) {
            if (device === 'mobile') return null;
            return <Button disabled>{t('rubric.generate-rubric', 'Generate Rubric')}</Button>;
        }

        if (result) {
            return (
                <Stack direction={'row'} spacing={4}>
                    <Button disabled={hasClickedSaveAsTemplate} variant={'secondary'} onClick={onClickSaveAsTemplate}>
                        {t('common:controls.save-as-template', 'Save as a Template')}
                    </Button>
                    <Button onClick={onClickCreateRubric}>{t('rubric.create-rubric', 'Create Rubric')}</Button>
                </Stack>
            );
        }

        return <Button onClick={onClickGenerate}>{t('rubric.generate-rubric', 'Generate Rubric')}</Button>;
    };

    useEffect(() => {
        if (!open) {
            isCancelled.current = false;
            setForm(defaultForm);
            setHasClickedSubmit(false);
            setIsLoading(false);
            setResult(null);
            setRubricErrors(defaultRubricErrors);
            setHasClickedSubmitRubric(false);
            setHasClickedSaveAsTemplate(false);
        }
    }, [open]);

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            {!(device === 'mobile' && (isLoading || result)) && (
                <CustomDialogTitle device={device}>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                            {t('rubric.create-ai-rubric.title', 'Create a Rubric')}
                        </Text>
                        <IconButton
                            aria-label={'close'}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleClose}
                        />
                    </Stack>
                </CustomDialogTitle>
            )}
            {device !== 'mobile' && <Divider />}
            <CustomDialogContent device={device}>{renderDialogContent()}</CustomDialogContent>
            {device !== 'mobile' && <Divider />}
            <CustomDialogActions device={device}>{renderDialogActions()}</CustomDialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogTitle = styled(DialogTitle)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomDialogContent = styled(DialogContent)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomDialogActions = styled(DialogActions)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const InputStack = styled(Stack)(({ device }) => {
    return {
        ...(device === 'mobile' && {
            width: '100%',
        }),
    };
});

const CustomSelect = styled(Select)(({ device }) => {
    return {
        '& > .MuiFormLabel-root': {
            display: 'flex',
        },
        '& > .MuiInputBase-root': {
            width: device === 'mobile' ? '100%' : '300px',
        },
    };
});

const CustomTooltip = styled(Tooltip)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomTextInput = styled(TextInput)(({ device }) => {
    return {
        width: device === 'mobile' ? '100%' : '300px',
    };
});

const LoadingStack = styled(Stack)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            position: 'relative',
            width: '100%',
            height: '100%',
        }),
    };
});

const CenterStack = styled(Stack)(({ device }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        ...(device === 'mobile' && {
            width: '80%',
        }),
    };
});

const ResultStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomButton = styled(Button)(() => {
    return {
        zIndex: 1,
        alignSelf: 'flex-end',
    };
});

const CustomEditRubricForm = styled(EditRubricForm)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            position: 'relative',
            top: '-56px',
        }),
    };
});

export default CreateRubricAIDialog;
